import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative h-screen min-h-[600px] sm:min-h-[800px] flex items-center justify-center overflow-hidden">
      {/* Background Image with Ken Burns Effect */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-transform duration-[20000ms] ease-out ${
          isLoaded ? 'scale-110' : 'scale-100'
        }`}
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/5aa94b232c247411e2938a964697e5a1.jpg)',
          filter: 'saturate(0.85)',
        }}
      />

      {/* Dark Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

      {/* Animated Lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        <div className={`absolute top-1/4 left-10 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}></div>
        <div className={`absolute top-1/3 right-20 w-px h-48 bg-gradient-to-b from-transparent via-white/15 to-transparent transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}></div>
        <div className={`absolute bottom-1/3 left-1/4 w-24 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent transition-all duration-1000 delay-1200 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 text-center w-full">
        <div
          className={`inline-block bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2 mb-6 transition-all duration-700 ease-out ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-white/90 text-xs sm:text-sm font-medium uppercase tracking-widest">
            Medical Imaging Equipment Leasing
          </span>
        </div>

        <h1
          className={`text-white text-[28px] sm:text-[42px] lg:text-[58px] font-light tracking-tight leading-tight mb-5 sm:mb-8 max-w-[960px] mx-auto transition-all duration-1000 ease-out ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Mobile MRI &amp; CT Equipment Leasing for Hospitals &amp; Health Systems
        </h1>

        <p
          className={`text-white/90 text-base sm:text-lg lg:text-[22px] font-normal leading-relaxed mb-8 sm:mb-12 max-w-[760px] mx-auto transition-all duration-1000 ease-out delay-200 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Expand your imaging capacity without capital investment. We lease, deliver, and maintain the equipment — your team operates it.
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap transition-all duration-1000 ease-out delay-400 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to="/contact"
            className="w-full sm:w-auto bg-white text-navy px-8 sm:px-10 py-3.5 sm:py-4 rounded-lg text-base font-medium hover:bg-white/95 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-center"
          >
            Request a Lease Quote
          </Link>
          <Link
            to="/services"
            className="w-full sm:w-auto bg-transparent text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-lg text-base font-medium border-2 border-white hover:bg-white hover:text-navy transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-center"
          >
            View Equipment
          </Link>
        </div>

        {/* Key Stats */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 mt-10 sm:mt-16 transition-all duration-1000 ease-out delay-600 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center">
            <div className="text-white text-2xl sm:text-3xl font-semibold">48–72hr</div>
            <div className="text-white/60 text-xs sm:text-sm mt-1">Deployment Capability</div>
          </div>
          <div className="w-px h-8 bg-white/20 hidden sm:block"></div>
          <div className="text-center">
            <div className="text-white text-2xl sm:text-3xl font-semibold">Nationwide</div>
            <div className="text-white/60 text-xs sm:text-sm mt-1">Equipment Delivery</div>
          </div>
          <div className="w-px h-8 bg-white/20 hidden sm:block"></div>
          <div className="text-center">
            <div className="text-white text-2xl sm:text-3xl font-semibold">Flexible</div>
            <div className="text-white/60 text-xs sm:text-sm mt-1">Short &amp; Long-Term Leases</div>
          </div>
        </div>
      </div>

      {/* Licensed Badge */}
      <div
        className={`absolute bottom-20 sm:bottom-8 right-4 sm:right-8 bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-lg px-4 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 transition-all duration-1000 ease-out delay-700 ${
          isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
        }`}
      >
        <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
          <i className="ri-shield-check-line text-white text-xl sm:text-2xl"></i>
        </div>
        <span className="text-white text-[11px] sm:text-xs font-medium whitespace-nowrap">
          Equipment Lessor &amp; Infrastructure Provider
        </span>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 hidden sm:flex transition-all duration-1000 ease-out delay-1000 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <span className="text-white/60 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
